import { useWorkspaceAnalyticsEmbed } from '@Pages/api/AnalyticsApi';
import { EmptyState } from '@Pages/pageRuntime/components/ui/EmptyState';
import { Skeleton } from '@Pages/pageRuntime/components/ui/Skeleton';
import { cn } from '@Pages/pageRuntime/lib/utils';

type AnalyticsEmbedProps = {
  /** Additional classes for the embed, e.g. a height override like `h-[40rem]`. */
  className?: string;
};

/**
 * Embeds the workspace's analytics dashboard (visitors, page views, referrers) — the
 * same dashboard as the workspace settings Analytics tab.
 *
 * The dashboard is only served to workspace system administrators; other viewers see a
 * placeholder instead, so this component is only meaningful on admin-facing pages.
 *
 * @example
 * <Sdk.Components.AnalyticsEmbed />
 */
export function AnalyticsEmbed({ className }: AnalyticsEmbedProps) {
  const { data, isLoading, isError } = useWorkspaceAnalyticsEmbed();

  const frameClasses = cn(
    'min-h-[32rem] w-full rounded-lg border border-border bg-background',
    className
  );

  if (isLoading) {
    return <Skeleton className={frameClasses} />;
  }

  // A null embed_url (no site provisioned) or a failed request — e.g. a 401/403 for a
  // non-administrator viewer — both render the placeholder rather than framing an auth or
  // error page. The server stays the sole authority on who may see analytics.
  const embedUrl = data?.embed_url ?? null;

  if (isError || embedUrl === null) {
    return (
      <div className={cn(frameClasses, 'flex items-center justify-center')}>
        <EmptyState
          title="Analytics dashboard unavailable"
          description="The analytics dashboard couldn't be loaded. It may not be set up for this workspace yet, or you may not have access to it."
        />
      </div>
    );
  }

  return (
    // Fathom's shared dashboard is a self-contained cross-origin app: it needs its real
    // origin (`allow-same-origin`) to fetch its own chart data, so we grant the curated
    // tokens it requires. Because the frame is cross-origin to this app, it cannot reach
    // back in to strip its own sandbox.
    // react-doctor-disable-next-line react-doctor/iframe-missing-sandbox
    <iframe
      src={embedUrl}
      title="Workspace analytics dashboard"
      loading="lazy"
      className={frameClasses}
      sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
    />
  );
}
